import { ArrowBack, Visibility, VisibilityOff } from "@mui/icons-material";
import { Box, Button, Checkbox, Chip, FormControl, FormControlLabel, FormLabel, IconButton, InputAdornment, InputLabel, OutlinedInput, Radio, RadioGroup, Stack, TextField, Typography } from "@mui/material";
import { ChangeEvent, FC, useEffect, useState } from "react";
import { AccountService } from "../../../services/AccountService";
import { AccountDTO } from "../../../types/AccountDTO";
import { useUtils } from "../../../utils/utilFunctions";
import { useAlert } from "../../contextProviders/AlertProvider";
import "./EditAccount.scss";

interface EditAccountProps {
	account: Partial<AccountDTO> | null;
	onClose: () => void;
}

const permissionGroups = {
	Geral: ['INICIO', 'EXECUTIONS', 'MANAGE_COMPARATIONS', 'SETTINGS', 'EVENTS', 'DOCUMENTS', 'NOTIFICATIONS'],
	Formulários: ['FORMS', 'CREATE_FORMS', 'CONCLUDE_FORMS', 'IMPORT_FORMS'],
	Funcionários: ['EMPLOYEES', 'SCHEDULING'],
	Denúncias: ['MANAGEMENT_COMPLAINTS', 'COMPLAINTS', 'CLASSIFY_COMPLAINTS', 'EXPORT_COMPLAINTS'],
	Auditoria: ['AUDIT', 'VIEW_AUDIT_DOCUMENTS', 'ADD_AUDIT_COMPANY', 'LGPD_POLICY_CONFIG', 'STATUS_ADMISSION'],
	EPIs: ['EPI_INSPECTION', 'CREATE_EPI_INSPECTION', 'CANCEL_EPI_INSPECTION', 'EPI_COMPLIANCE', 'EPI_NONCONFORMITY', 'RESOLVE_EPI_ACTION', 'EPI_DASHBOARD', 'EXPORT_EPI_REPORT', 'MANAGE_EPI_COMPANIES'],
	'Avaliação de riscos': ['RISK_ASSESSMENT'],
	Financeiro: ['FINANCIAL'],
};

const allPermissions = new Map<string, string>([
	['EXECUTIONS', 'Execuções'],
	['MANAGE_COMPARATIONS', 'Atualizar e ignorar divergências das comparações'],
	['INICIO', 'Início'],
	['FORMS', 'Módulo Formulários'],
	['CREATE_FORMS', 'Criar Formulários'],
	['CONCLUDE_FORMS', 'Concluir Formulários'],
	['IMPORT_FORMS', 'Importar Formulários'],
	['SETTINGS', 'Configurações'],
	['EVENTS', 'Execuções de eventos'],
	['DOCUMENTS', 'Envio de documentos'],
	['COMPLAINTS', 'Visualizar denúncias'],
	['MANAGEMENT_COMPLAINTS', 'Gerenciar canais de denúncia'],
	['EXPORT_COMPLAINTS', 'Exportar Denúncias'],
	['CLASSIFY_COMPLAINTS', 'Classificar Denúncias'],
	['EMPLOYEES', 'Módulo cadastro de funcionários'],
	['SCHEDULING', 'Agendar exames admissionais'],
	['NOTIFICATIONS', 'Notificações'],
	['AUDIT', 'Módulo de auditoria de admissões'],
	['ADD_AUDIT_COMPANY', 'Adicionar empesas para auditoria'],
	['VIEW_AUDIT_DOCUMENTS', 'Visualizar documentos de admissão'],
	['LGPD_POLICY_CONFIG', 'Configurações & LGPD'],
	['STATUS_ADMISSION', 'Alterar status da admissão'],
	['EPI_INSPECTION', 'Módulo de inspeção de EPIs'],
	['CREATE_EPI_INSPECTION', 'Realizar inspeção de EPI'],
	['CANCEL_EPI_INSPECTION', 'Cancelar e retificar inspeção'],
	['EPI_COMPLIANCE', 'Gestão da conformidade'],
	['EPI_NONCONFORMITY', 'Não conformidades e plano de ação'],
	['RESOLVE_EPI_ACTION', 'Concluir ação corretiva'],
	['EPI_DASHBOARD', 'Dashboard NR-06'],
	['EXPORT_EPI_REPORT', 'Exportar relatório de evidências'],
	['MANAGE_EPI_COMPANIES', 'Configurar empresas do módulo de EPIs'],
	['RISK_ASSESSMENT', 'Módulo de avaliação de riscos'],
	['FINANCIAL', 'Módulo financeiro']
]);

const EditAccount: FC<EditAccountProps> = ({ account, onClose }) => {
	const [name, setName] = useState(account?.name || '');
	const [userType, setUserType] = useState(account?.userType || '');
	const [permissionType, setPermissionType] = useState(account?.permissionType || '');
	const [regionId, setRegionId] = useState(account?.regionId || undefined);
	const [cnpj, setCnpj] = useState('');
	const [permissionCnpjs, setPermissionCnpjs] = useState<string[]>(account?.permissionCnpjs || []);
	const [modules, setModules] = useState(account?.modules || '');
	const [username, setUsername] = useState(account?.username || '');
	const [password, setPassword] = useState('');
	const [showPassword, setShowPassword] = useState(false);
	const { showAlert, showError } = useAlert();
	const { formatDocument } = useUtils();

	const isEditingExistingAccount = !!account?.username;

	const disabledSave = (!isEditingExistingAccount && !password) || !userType || !modules
		|| (userType === 'EXTERNO' && ((permissionType === 'REGION' && !regionId) || (permissionType === 'CNPJ' && !permissionCnpjs?.length)));

	const handleClickShowPassword = () => setShowPassword((show) => !show);

	const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
		event.preventDefault();
	};
	const handleMouseUpPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
		event.preventDefault();
	};

	const handleModuleChange = (moduleKey: string) => {
		const currentModules = modules ? modules.split(',').filter(Boolean) : [];
		const newModules = currentModules.includes(moduleKey)
			? currentModules.filter(m => m !== moduleKey)
			: [...currentModules, moduleKey];
		setModules(newModules.join(','));
	};

	const handleAddCnpj = () => {
		if (!cnpj || cnpj.includes('_')) return;
		if (!permissionCnpjs) {
			setPermissionCnpjs([cnpj]);
		} else if (!permissionCnpjs.includes(cnpj)) {
			setPermissionCnpjs([...permissionCnpjs, cnpj]);
		}
		setCnpj('');
	}

	useEffect(() => {
		setName(account?.name || '');
		setUsername(account?.username || '');
		setUserType(account?.userType || '');
		setRegionId(account?.regionId || undefined);
		setModules(account?.modules || '');
		setPermissionType(account?.permissionType || '');
		setPermissionCnpjs(account?.permissionCnpjs || []);
		setPassword('');
	}, [account]);

	const save = async () => {
		const tag = isEditingExistingAccount ? 'alterado' : 'criado';
		const service = new AccountService();
		const accountToSave = {
			name: name,
			username: username,
			password: password,
			userType: userType,
			permissionType: permissionType,
			permissionCnpjs: permissionCnpjs,
			regionId: regionId,
			modules: modules
		} as AccountDTO;
		const response = await service.edit(accountToSave);
		if (response?.status === 200) {
			showAlert(`Usuário ${tag} com sucesso`, "success");
			onClose();
		} else {
			showError();
		}
	}

	return (
		<Box className="edit-account-form">
			<Box className="form-header">
				<IconButton onClick={onClose} color="inherit" sx={{ mr: 2 }}>
					<ArrowBack />
				</IconButton>
				<Typography variant="h6">{isEditingExistingAccount ? 'Editar' : 'Criar'} usuário</Typography>
			</Box>
			<Box className="form-content">
				<Box className="form-column">
					<TextField
						size="small"
						fullWidth
						label="Nome do usuário"
						id="account-name"
						value={name}
						autoComplete="new-password"
						onChange={(e: ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
					/>
					<TextField
						size="small"
						fullWidth
						label="Login do usuário"
						id="account-username"
						value={username}
						autoComplete="new-password"
						disabled={isEditingExistingAccount}
						onChange={(e: ChangeEvent<HTMLInputElement>) => setUsername(e.target.value)}
					/>
					<FormControl fullWidth variant="outlined" size="small">
						<InputLabel htmlFor="outlined-adornment-password">Nova senha</InputLabel>
						<OutlinedInput
							id="outlined-adornment-password"
							type={showPassword ? 'text' : 'password'}
							value={password}
							autoComplete="new-password"
							onChange={(e: ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
							endAdornment={
							<InputAdornment position="end">
								<IconButton
								aria-label={showPassword ? 'Ocultar senha' : 'Exibir senha'}
								onClick={handleClickShowPassword}
								onMouseDown={handleMouseDownPassword}
								onMouseUp={handleMouseUpPassword}
								edge="end"
								>
								{showPassword ? <Visibility /> : <VisibilityOff />}
								</IconButton>
							</InputAdornment>
							}
							label="Nova senha"
						/>
					</FormControl>
				</Box>
				<Box className="form-column">
					<FormControl>
						<FormLabel id="user-type-label">Tipo de usuário:</FormLabel>
						<RadioGroup
							row
							aria-labelledby="user-type-label"
							value={userType}
							onChange={(e: ChangeEvent<HTMLInputElement>) => setUserType(e.target.value)}
						>
							<FormControlLabel value="INTERNO" control={<Radio />} label="Interno" />
							<FormControlLabel value="EXTERNO" control={<Radio />} label="Externo" />
						</RadioGroup>
					</FormControl>
					{userType === 'EXTERNO' && (
						<>
							<FormControl>
								<FormLabel id="permission-type-label">Limitar acesso por:</FormLabel>
								<RadioGroup
									row
									aria-labelledby="permission-type-label"
									value={permissionType}
									onChange={(e: ChangeEvent<HTMLInputElement>) => setPermissionType(e.target.value)}
								>
									<FormControlLabel value="REGION" control={<Radio />} label="Região" />
									<FormControlLabel value="CNPJ" control={<Radio />} label="CPF/CNPJ" />
								</RadioGroup>
							</FormControl>
							{permissionType === 'REGION' && (
								<TextField
									fullWidth
									size="small"
									label="ID da região"
									id="region-id"
									type="number"
									value={regionId}
									onChange={(e: ChangeEvent<HTMLInputElement>) => setRegionId(Number(e.target.value))}
								/>
							)}
							{permissionType === 'CNPJ' && (
								<>
									<Box sx={{display: "flex", gap: "1rem", alignItems: "center"}}>
										<TextField
											fullWidth
											label="CFP/CNPJ"
											id="cnpj-form"
											size="small"
											inputProps={{ maxLength: 18}}
											value={cnpj}
											onChange={(e: ChangeEvent<HTMLInputElement>) => setCnpj(formatDocument(e.target.value.trim()))}
										/>
										<Button onClick={handleAddCnpj} disabled={!cnpj || cnpj.includes('_')} >Adicionar</Button>
									</Box>
									<Stack
										direction="row"
										spacing={1}
										sx={{
											flexWrap: "wrap",
											width: '100%',
											display: permissionCnpjs?.length
												? "flex"
												: "none",
											gap: "5px"
										}}
									>
										{permissionCnpjs.map((cnpjItem) => (
											<Chip
												key={cnpjItem}
												label={cnpjItem}
												onDelete={() => {
													setPermissionCnpjs(permissionCnpjs.filter(item => item !== cnpjItem));
												}}
											/>
										))}
									</Stack>
								</>
							)}
						</>
					)}
				</Box>
				<Box className="permissions-container full-width-grid-item">
					<FormLabel component="legend">Módulos e permissões:</FormLabel>
					{Object.entries(permissionGroups).map(([groupName, groupPermissions]) => (
						<Box key={groupName} className="permission-group">
							<Typography variant="subtitle1" className="permission-group-title">{groupName}</Typography>
							<Box className="checkbox-grid">
								{groupPermissions.map(key => (
									<FormControlLabel
										key={key}
										control={
											<Checkbox
												checked={(modules || '').split(',').includes(key)}
												onChange={() => handleModuleChange(key)}
												size="small"
											/>
										}
										label={allPermissions.get(key)}
									/>
								))}
							</Box>
						</Box>
					))}
				</Box>
			</Box>
			<Box className="form-actions">
				<Button id="edit-button" onClick={onClose}>
					Fechar
				</Button>
				<Button
					disabled={disabledSave}
					id="edit-button"
					color="success"
					onClick={save}
				>
					Salvar
				</Button>
			</Box>
		</Box>
	);
}

export default EditAccount;